import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import ParcelMap, { plotSize } from '../../components/ParcelMap.jsx';
import { DataTable, ErrorState, Spinner, StatusBadge } from '../../components/ui.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { formatXaf } from '../../lib/format.js';

/**
 * The public land register: every verified plot, where it is and where it
 * stands. Somebody about to buy can look a plot up by its number, its land
 * certificate or its village and see whether it is registered, to how many
 * owners it has passed, and whether anybody has raised a conflict over it - all
 * without seeing who owns it.
 */
const LandRegistry = () => {
  useTitle('Land register');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [term, setTerm] = useState(params.get('q') || '');
  const query = params.get('q') ? `?q=${encodeURIComponent(params.get('q'))}` : '';
  const parcels = useFetch(`/land/parcels${query}`, [query]);
  const items = parcels.data?.items || [];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>Land register</h1>
          <p className="muted">
            Every plot sold here is registered once, from the site plan its village chief signed, and checked by our team.
            A plot on the register cannot be sold by anybody but its owner.
          </p>
        </div>
        <Link to="/search?kind=sale" className="btn btn-primary">
          See what is for sale
        </Link>
      </header>

      <form
        className="register-search"
        onSubmit={(event) => {
          event.preventDefault();
          setParams(term.trim() ? { q: term.trim() } : {});
        }}
      >
        <input
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Plot reference, plot number, land certificate number or village"
          aria-label="Look up a plot"
        />
        <button type="submit" className="btn btn-primary">
          Look it up
        </button>
      </form>

      <div className="notice notice-info">
        Buying land? Look the plot up here first. If it is not on the register, or the seller's papers do not match what is
        shown, do not pay anything.
      </div>

      {parcels.loading && !parcels.data ? <Spinner /> : null}
      {parcels.error ? <ErrorState message={parcels.error} onRetry={parcels.reload} /> : null}

      {parcels.data ? (
        <>
          {items.length ? (
            <ParcelMap
              height={440}
              plots={items.map((plot) => ({
                id: plot.id,
                boundary: plot.boundary,
                tone: plot.conflictOpen ? 'clash' : 'plot',
                label: `${plot.reference} - ${plot.village}${plot.forSale ? ' - for sale' : ''}`,
                onClick: () => navigate(`/land/${plot.id}`)
              }))}
            />
          ) : null}

          <DataTable
            columns={[
              {
                key: 'plot',
                header: 'Plot',
                render: (plot) => (
                  <div className="cell-stack">
                    <Link to={`/land/${plot.id}`} className="cell-title">
                      {plot.reference}
                    </Link>
                    <span className="muted">
                      {plot.village}
                      {plot.city ? `, ${plot.city}` : ''}
                      {plot.plotNumber ? ` - plot ${plot.plotNumber}` : ''}
                    </span>
                  </div>
                )
              },
              { key: 'size', header: 'Size', render: (plot) => plotSize(plot.areaSqm) },
              { key: 'certificate', header: 'Land certificate', render: (plot) => plot.certificateNumber || <span className="muted">None given</span> },
              {
                key: 'state',
                header: 'Now',
                render: (plot) =>
                  plot.conflictOpen ? (
                    <StatusBadge status="rejected" label="Conflict reported" />
                  ) : plot.forSale ? (
                    <Link to={`/properties/${plot.forSale.propertyId}`}>For sale, {formatXaf(plot.forSale.price)}</Link>
                  ) : plot.transferPending ? (
                    <StatusBadge status="pending" label="Changing hands" />
                  ) : (
                    <StatusBadge status="approved" label="Registered" />
                  )
              }
            ]}
            rows={items}
            empty={{
              title: params.get('q') ? 'No registered plot matches that' : 'No plots registered yet',
              message: params.get('q')
                ? 'If a seller says their land is registered and it is not here, ask why before you go any further.'
                : 'Plots appear here once they are registered and checked.'
            }}
          />
        </>
      ) : null}
    </div>
  );
};

export default LandRegistry;
